import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { requireSupabase } from '../../lib/supabase';
import type { Entitlement, Profile, Volume } from '../../types/hub';

type EntitlementWithRelations = Entitlement & { profiles?: Pick<Profile, 'full_name' | 'email'>; volumes?: Pick<Volume, 'short_title'> };

export function AdminEntitlementsPage() {
  const [members, setMembers] = useState<Profile[]>([]);
  const [volumes, setVolumes] = useState<Volume[]>([]);
  const [entitlements, setEntitlements] = useState<EntitlementWithRelations[]>([]);
  const [userId, setUserId] = useState('');
  const [volumeId, setVolumeId] = useState('');
  const [resourceKey, setResourceKey] = useState('volume_content');
  const [accessType, setAccessType] = useState<'lifetime' | 'timed' | 'manual'>('lifetime');
  const [days, setDays] = useState('90');
  const [message, setMessage] = useState('');

  const reload = async () => {
    const client = requireSupabase();
    const [memberResult, volumeResult, entitlementResult] = await Promise.all([
      client.from('profiles').select('*').order('full_name'),
      client.from('volumes').select('*').order('sort_order'),
      client.from('entitlements').select('*, profiles(full_name,email), volumes(short_title)').order('created_at', { ascending: false }),
    ]);
    if (memberResult.error) throw memberResult.error;
    if (volumeResult.error) throw volumeResult.error;
    if (entitlementResult.error) throw entitlementResult.error;
    setMembers((memberResult.data ?? []) as Profile[]);
    setVolumes((volumeResult.data ?? []) as Volume[]);
    setEntitlements((entitlementResult.data ?? []) as EntitlementWithRelations[]);
  };

  useEffect(() => { reload().catch((error) => setMessage(error.message)); }, []);
  useEffect(() => { if (!volumeId && volumes[0]) setVolumeId(volumes[0].id); }, [volumes, volumeId]);

  const endsAt = useMemo(() => {
    if (accessType !== 'timed') return null;
    const value = Number(days);
    if (!Number.isFinite(value) || value <= 0) return null;
    const date = new Date(); date.setDate(date.getDate() + value); return date.toISOString();
  }, [accessType, days]);

  const grant = async (event: FormEvent) => {
    event.preventDefault(); setMessage('');
    if (!userId || !volumeId) return setMessage('Choose a member and volume.');
    const { error } = await requireSupabase().rpc('admin_grant_entitlement', {
      target_user: userId,
      target_volume: volumeId,
      target_resource_key: resourceKey,
      target_type: accessType,
      target_source: 'admin_console',
      target_ends_at: endsAt,
      target_is_lifetime: accessType === 'lifetime',
      target_product: null,
      target_notes: 'Granted from Phase 1 admin console',
    });
    if (error) return setMessage(error.message);
    setMessage('Entitlement granted and logged.');
    await reload();
  };

  const revoke = async (id: string) => {
    if (!window.confirm('Revoke this entitlement?')) return;
    const { error } = await requireSupabase().rpc('admin_revoke_entitlement', { target_entitlement: id, reason: 'Revoked from Phase 1 admin console' });
    if (error) setMessage(error.message); else { setMessage('Entitlement revoked and logged.'); await reload(); }
  };

  return (
    <div className="hub-page">
      <header className="page-header"><p className="eyebrow">Admin · Entitlements</p><h1>Volume-specific access</h1><p>All grants and revocations run through SECURITY DEFINER RPCs that verify the caller’s role and write an audit record.</p></header>
      <form className="card entitlement-form" onSubmit={grant}>
        <label>Member<select value={userId} onChange={(e) => setUserId(e.target.value)} required><option value="">Choose member</option>{members.map((member) => <option key={member.id} value={member.id}>{member.full_name || member.email}</option>)}</select></label>
        <label>Volume<select value={volumeId} onChange={(e) => setVolumeId(e.target.value)} required>{volumes.map((volume) => <option key={volume.id} value={volume.id}>{volume.short_title}</option>)}</select></label>
        <label>Resource<select value={resourceKey} onChange={(e) => setResourceKey(e.target.value)}><option value="volume_content">Volume content</option><option value="delphi">Delphi</option><option value="calls">Weekly calls</option><option value="cohort_questions">Cohort questions</option><option value="kickoff_intensive">Kickoff Intensive</option></select></label>
        <label>Access type<select value={accessType} onChange={(e) => setAccessType(e.target.value as typeof accessType)}><option value="lifetime">Lifetime</option><option value="timed">Timed</option><option value="manual">Manual</option></select></label>
        {accessType === 'timed' && <label>Duration in days<input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} /></label>}
        <button className="button primary">Grant entitlement</button>
      </form>
      {message && <div className="form-message success admin-message">{message}</div>}
      <div className="table-card"><table><thead><tr><th>Member</th><th>Volume</th><th>Resource</th><th>Type</th><th>Status</th><th>Expires</th><th /></tr></thead><tbody>{entitlements.map((item) => <tr key={item.id}><td>{item.profiles?.full_name || item.profiles?.email || item.user_id.slice(0, 8)}</td><td>{item.volumes?.short_title || 'Shared'}</td><td>{item.resource_key}</td><td>{item.entitlement_type}</td><td>{item.active ? 'Active' : 'Inactive'}</td><td>{item.is_lifetime ? 'Lifetime' : item.ends_at ? new Date(item.ends_at).toLocaleDateString() : 'Open'}</td><td>{item.active && <button className="table-action danger" onClick={() => revoke(item.id)}>Revoke</button>}</td></tr>)}</tbody></table></div>
    </div>
  );
}
